import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApi } from '@/shared/api';
import type {
    DeleteApiServicesServiceIdFormulasFormulaId200,
    GetApiServicesServiceIdFormulas200FormulasItem,
    PostApiServicesServiceIdFormulas201,
    PutApiServicesServiceIdFormulasFormulaId200,
} from '@/shared/api/models';

export type ServiceFormulaRow = GetApiServicesServiceIdFormulas200FormulasItem;

export type UpsertServiceFormulaInput = {
    productId: string;
    consumeQty: number;
    isActive?: boolean;
    /** When set, updates the service-level POS stock-deduction flag (mirrored on all formula rows in API responses). */
    trackInventoryConsumption?: boolean;
};

export const serviceFormulasQueryKey = (serviceId: string) => ['services', serviceId, 'formulas'] as const;

export const useGetServiceFormulas = (serviceId?: string, enabled: boolean = true) => {
    const api = getApi();

    return useQuery({
        queryKey: serviceId ? serviceFormulasQueryKey(serviceId) : ['services', 'formulas', 'missing-service-id'],
        queryFn: () => api.getApiServicesServiceIdFormulas(String(serviceId)),
        enabled: enabled && !!serviceId,
        staleTime: 60 * 1000,
        retry: 1,
    });
};

export const useCreateServiceFormula = (serviceId: string) => {
    const api = getApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: UpsertServiceFormulaInput) => {
            const body = {
                formulas: [
                    {
                        productId: input.productId,
                        consumeQty: input.consumeQty,
                        isActive: input.isActive ?? true,
                    },
                ],
                ...(input.trackInventoryConsumption !== undefined
                    ? { trackInventoryConsumption: input.trackInventoryConsumption }
                    : {}),
            };
            return api.postApiServicesServiceIdFormulas(
                serviceId,
                body,
            ) as Promise<PostApiServicesServiceIdFormulas201>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: serviceFormulasQueryKey(serviceId) });
        },
    });
};

export const useUpdateServiceFormula = (serviceId: string) => {
    const api = getApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ formulaId, input }: { formulaId: string; input: UpsertServiceFormulaInput }) => {
            const body = {
                productId: input.productId,
                consumeQty: input.consumeQty,
                isActive: input.isActive ?? true,
                ...(input.trackInventoryConsumption !== undefined
                    ? { trackInventoryConsumption: input.trackInventoryConsumption }
                    : {}),
            };
            return api.putApiServicesServiceIdFormulasFormulaId(
                serviceId,
                formulaId,
                body,
            ) as Promise<PutApiServicesServiceIdFormulasFormulaId200>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: serviceFormulasQueryKey(serviceId) });
        },
    });
};

export const useDeleteServiceFormula = (serviceId: string) => {
    const api = getApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (formulaId: string) => {
            return api.deleteApiServicesServiceIdFormulasFormulaId(
                serviceId,
                formulaId,
            ) as Promise<DeleteApiServicesServiceIdFormulasFormulaId200>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: serviceFormulasQueryKey(serviceId) });
        },
    });
};
